// Pixar Singing Ad's three stages (live's `lyrics-section`, `song-stage` and `cast-stage`, and the
// shared `song-take-card`; live's Song and Cast & set stages were never captured, so built from
// live's code): the lyrics with their syllable caps, the song's takes picked by ear, and the cast
// and set with the shots' timing line.
import { ActionIcon, Button, Group, Loader, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { ArrowLeft, Lock, Music, PencilLine, Plus, WandSparkles } from "lucide-react";
import { useState, type ReactNode } from "react";

import type {
  PixarSingingDraftStill,
  PixarSingingStillName,
  PixarLyrics,
  PixarSongTake,
  PixarSingingStillTiming,
} from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { GENERATE_SCRIPT as GENERATE_SCRIPT_LABEL } from "../../../platform/scripts/ScriptActions";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { FieldLabel } from "../../../ui/FieldLabel";
import { StillCard } from "./StillCard";
import { TakeCard } from "./TakeCard";
import { STILL_LABELS } from "./pixar-singing-draft";
import {
  LYRIC_PARTS,
  PUNCH_LINE_BEAT,
  PUNCH_LINE_MAX_SYLLABLES,
  TURN_INDEX,
  countSyllables,
} from "./pixar-singing-lyrics";
import classes from "./podcast.module.css";
import pixar from "./pixar-singing.module.css";

/** Live's words (`library-view/constants.ts`, `song-take-card/constants.ts`). */
const LYRICS_TOOLTIP =
  '6 fixed lines sung by one singer: a sweet setup, a flat "said no one ever", then two rhyming ' +
  "couplets ending on the brand — and a punch line spoken over the final chord as the end card " +
  "appears. At most 46 syllables in all; every product fact is quoted from the product text.";
const WRITE_LYRICS = "Write the 6 lines yourself";
const TURN_LOCKED = "Always exactly these words";
const SONG_TOOLTIP =
  "Three takes of the jingle, each checked against the lyrics. Listen and pick one — its timing " +
  "sets the shots, the cut to the end card and the captions. A failed check can be a " +
  "transcription slip, so listen before you skip a take.";
const MORE_TAKES = "One more take with the same prompt (at most six)";
const UNPLANNABLE_TAKE = "A whole line of this take was not heard, so the shots cannot be timed to it.";
const BACK_TO_TAKES = "Back to takes";
const BACK_TO_TAKES_CONFIRM = "Pick another take? The shot plan and the images will be made again.";
/** Live's cap on a line as typed (`MAX_SCRIPT_LINE_LENGTH`). */
const MAX_LINE_LENGTH = 500;

export interface LyricsSectionProps {
  lyrics: PixarLyrics | null;
  /** Live's meta line (`toLyricsMeta`), with the lyrics. */
  meta: string | null;
  writing: boolean;
  locked: boolean;
  /** What keeps the lyrics from approval, once they were edited (live's error line). */
  issue: string | null;
  /** Live's warnings under it: the pack hard to read, the lyrics' check failing twice. */
  warning: string | null;
  onGenerate: () => void;
  onWrite: () => void;
  onLine: (index: number, text: string) => void;
  onTag: (text: string) => void;
}

/** One row of the editor: its number or marker, its beat, the words, their syllables. */
function LyricRow(props: {
  marker: ReactNode;
  beat: string;
  text: string;
  cap: number | null;
  label: string;
  fixed: boolean;
  locked: boolean;
  onChange: (text: string) => void;
}) {
  const { marker, beat, text, cap, label, fixed, locked, onChange } = props;
  const syllables = countSyllables(text);
  return (
    <div className={classes.line}>
      <span className={classes.number}>{marker}</span>
      <span className={classes.beat} title={beat}>
        {beat}
      </span>
      {fixed ? (
        <Group gap={6} wrap="nowrap" className={classes.lineText} aria-label="Fixed line">
          <Text size="sm">{text}</Text>
          <Tooltip label={TURN_LOCKED} withArrow>
            <Lock size={12} aria-label={TURN_LOCKED} />
          </Tooltip>
        </Group>
      ) : (
        <Textarea
          className={classes.lineText}
          classNames={{ input: classes.lineInput }}
          variant="unstyled"
          autosize
          minRows={1}
          maxLength={MAX_LINE_LENGTH}
          aria-label={label}
          value={text}
          disabled={locked}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      )}
      {cap === null ? (
        <span className={classes.count} />
      ) : (
        <span className={classes.count} data-over={syllables > cap || undefined}>
          {syllables}/{cap}
        </span>
      )}
    </div>
  );
}

/**
 * Live's lyrics section: "Lyrics" with its info tooltip; at the right the meta line, the wand
 * (live's shared "Generate Script") and the pencil. Without lyrics, live's hint; with them, a row a
 * line (its number, its beat, the words, their syllables against the part's cap, red over it),
 * the turn fixed with a lock, then the punch line under a note.
 */
export function LyricsSection(props: LyricsSectionProps) {
  const { lyrics, meta, writing, locked, issue, warning, onGenerate, onWrite, onLine, onTag } = props;
  return (
    <section aria-label="Lyrics">
      <div className={classes.scriptHeader}>
        <Text component="div" className={classes.scriptTitle}>
          <FieldLabel label="Lyrics" info={LYRICS_TOOLTIP} spoken />
        </Text>
        <Group gap={8} wrap="nowrap">
          {meta === null ? null : <Text className={classes.meta}>{meta}</Text>}
          <Tooltip label={GENERATE_SCRIPT_LABEL} withArrow>
            <ActionIcon
              variant="subtle"
              size={36}
              aria-label={GENERATE_SCRIPT_LABEL}
              disabled={locked}
              onClick={onGenerate}
            >
              <WandSparkles size={16} aria-hidden />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={WRITE_LYRICS} withArrow>
            <ActionIcon variant="subtle" aria-label={WRITE_LYRICS} disabled={locked} onClick={onWrite}>
              <PencilLine size={16} aria-hidden />
            </ActionIcon>
          </Tooltip>
        </Group>
      </div>
      <div className={classes.editor} data-busy={writing || undefined}>
        {lyrics === null ? (
          <Text className={classes.hint}>
            Press
            <WandSparkles size={14} className={classes.hintIcon} aria-label="the wand" />
            to write the 6 lines from the product, or
            <PencilLine size={14} className={classes.hintIcon} aria-label="the pencil" />
            to write them yourself.
          </Text>
        ) : (
          <div className={classes.lines}>
            {lyrics.lines.map((line, index) => {
              const part = LYRIC_PARTS[index];
              const beat = part?.beat ?? "";
              return (
                <LyricRow
                  key={line.id}
                  marker={index + 1}
                  beat={beat}
                  text={line.text}
                  cap={part?.cap ?? null}
                  label={`Line ${index + 1}, ${beat}`}
                  fixed={index === TURN_INDEX}
                  locked={locked}
                  onChange={(text) => onLine(index, text)}
                />
              );
            })}
            <LyricRow
              marker={<Music size={12} aria-hidden />}
              beat={PUNCH_LINE_BEAT}
              text={lyrics.tag}
              cap={PUNCH_LINE_MAX_SYLLABLES}
              label="Punch line"
              fixed={false}
              locked={locked}
              onChange={onTag}
            />
          </div>
        )}
        {writing ? (
          <div className={classes.editorLoader}>
            <Loader size="sm" aria-label="Writing the lyrics" />
          </div>
        ) : null}
      </div>
      {issue === null ? null : <Text className={classes.issue}>{issue}</Text>}
      {warning === null ? null : <Text className={classes.warning}>{warning}</Text>}
    </section>
  );
}

export interface SongStageProps {
  takes: readonly PixarSongTake[];
  /** The approved lyrics' song, in whole seconds (live's "{S} s asked"). */
  seconds: number;
  note: string | null;
  picked: number | null;
  busy: boolean;
  onPick: (number: number) => void;
  onAddTake: () => void;
}

/** Live's song stage: "Song" with its info tooltip, "{n} takes · {S} s asked" and live's "+"
 * (off at six takes or while one is made), a card a take, then live's note on the takes. */
export function SongStage(props: SongStageProps) {
  const { takes, seconds, note, picked, busy, onPick, onAddTake } = props;
  const making = takes.some((take) => take.status === "working");
  return (
    <Stack gap={12} component="section" aria-label="Song">
      <div className={classes.scriptHeader}>
        <Text component="div" className={classes.scriptTitle}>
          <FieldLabel label="Song" info={SONG_TOOLTIP} spoken />
        </Text>
        <Group gap={8} wrap="nowrap">
          <Text className={classes.meta}>
            {takes.length} takes · {seconds} s asked
          </Text>
          <Tooltip label={MORE_TAKES} withArrow>
            <span>
              <ActionIcon
                variant="subtle"
                aria-label={MORE_TAKES}
                disabled={takes.length >= 6 || making || busy}
                onClick={onAddTake}
              >
                <Plus size={16} aria-hidden />
              </ActionIcon>
            </span>
          </Tooltip>
        </Group>
      </div>
      <Stack gap={8}>
        {takes.map((take) => (
          <TakeCard
            key={take.number}
            take={take}
            picked={picked === take.number}
            busy={busy}
            unpickable={UNPLANNABLE_TAKE}
            details={null}
            onPick={() => onPick(take.number)}
          />
        ))}
      </Stack>
      {note === null ? null : <Text className={pixar.note}>{note}</Text>}
    </Stack>
  );
}

const PENDING = { status: "pending", url: null, problem: null } as const;

/** A made still in the shared lightbox, named by live's label. */
function stillItem(still: PixarSingingDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

/** Live's timing line: "{label} {start}–{end} s" a shot, then the end card, joined by " · ". */
export function timingLine(timings: readonly PixarSingingStillTiming[]): string {
  return timings.map((each) => `${STILL_LABELS[each.name]} ${each.start_s}–${each.end_s} s`).join(" · ");
}

export interface CastStageProps {
  stills: readonly PixarSingingDraftStill[];
  timings: readonly PixarSingingStillTiming[];
  waiting: (name: PixarSingingStillName) => boolean;
  busy: boolean;
  onRegenerate: (name: PixarSingingStillName) => void;
  onBackToTakes: () => void;
}

/**
 * Live's cast stage: "Cast & set" and "Back to takes" (with live's confirmation), the plan's
 * stills (the character sheet, a frame a shot, the end card), each with ↻ and the shared
 * lightbox, then the shots' timing line.
 */
export function CastStage(props: CastStageProps) {
  const { stills, timings, waiting, busy, onRegenerate, onBackToTakes } = props;
  const [preview, setPreview] = useState<PixarSingingStillName | null>(null);
  const [confirming, setConfirming] = useState(false);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label="Cast & set" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>Cast &amp; set</Text>
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<ArrowLeft size={14} aria-hidden />}
          disabled={busy}
          onClick={() => setConfirming(true)}
        >
          {BACK_TO_TAKES}
        </Button>
      </Group>
      <div className={classes.stills}>
        {stills.map((still) => (
          <StillCard
            key={still.name}
            label={STILL_LABELS[still.name]}
            still={still.status === "pending" ? PENDING : still}
            waiting={waiting(still.name)}
            busy={busy}
            onRegenerate={() => onRegenerate(still.name)}
            onFixLabel={null}
            onPreview={() => setPreview(still.name)}
          />
        ))}
      </div>
      <Text className={pixar.timing}>{timingLine(timings)}</Text>
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as PixarSingingStillName | null)}
      />
      <ConfirmModal
        opened={confirming}
        title={BACK_TO_TAKES}
        confirmLabel={BACK_TO_TAKES}
        pending={false}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onBackToTakes();
          setConfirming(false);
        }}
      >
        <Text size="sm">{BACK_TO_TAKES_CONFIRM}</Text>
      </ConfirmModal>
    </Stack>
  );
}
